import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { isIOS, isAndroid } from '@/utils/platform';
// ✅ FIX: Remove illegal hook import - toast will be passed as callback
import { 
  getOneSignalAdapter, 
  getNotificationPermissionStatus,
  saveOneSignalUserId,
  OSNotificationAction
} from './index';

export interface OneSignalAuthResult {
  success: boolean;
  permissionGranted: boolean;
  oneSignalUserId?: string;
  message: string;
  requiresPermissionRequest?: boolean;
  isNewDevice?: boolean;
  wasExistingUser?: boolean;
}

export interface OneSignalPermissionRequest {
  title: string;
  message: string;
  allowButtonText: string;
  denyButtonText: string;
}

// ✅ FIX: Add toast callback interface
export interface ToastCallback {
  (options: {
    title: string;
    description: string;
    variant?: 'default' | 'destructive';
  }): void;
}

/**
 * FIXED: Enhanced OneSignal Authentication Flow with Proper External ID Binding
 * This prevents OneSignal from creating new anonymous users by setting External ID BEFORE initialization
 */
export const enhancedOneSignalLogin = async (
  supabaseUserId: string,
  options: {
    requestPermissionImmediately?: boolean;
    customPermissionRequest?: OneSignalPermissionRequest;
    silentMode?: boolean;
    showSuccessToast?: boolean;
    showToast?: ToastCallback; // ✅ FIX: Add toast callback
  } = {}
): Promise<OneSignalAuthResult> => {
  const {
    requestPermissionImmediately = false,
    customPermissionRequest,
    silentMode = false,
    showSuccessToast = true,
    showToast // ✅ FIX: Extract toast callback
  } = options;

  try {
    // Step 1: Platform check
    if (!Capacitor.isNativePlatform()) {
      
      return { 
        success: false, 
        permissionGranted: false,
        message: 'OneSignal is only available on mobile devices' 
      };
    }

    // Step 2: Environment check
    const oneSignalAppId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!oneSignalAppId) {
      
      return { 
        success: false, 
        permissionGranted: false,
        message: 'OneSignal configuration missing' 
      };
    }

    // Step 3: Initialize OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      
      return { 
        success: false, 
        permissionGranted: false,
        message: 'OneSignal service unavailable' 
      };
    }

    

    // CRITICAL FIX: Check if OneSignal is already initialized with wrong External ID
    let wasExistingUser = false;
    try {
      const currentDeviceState = await adapter.getDeviceState();
      if (currentDeviceState?.userId) {
        
        
        // Check if this is the correct External ID
        const currentExternalId = currentDeviceState.externalUserId;
        if (currentExternalId && currentExternalId !== supabaseUserId) {
          
          await adapter.logout();
          // Wait for logout to complete
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else if (currentExternalId === supabaseUserId) {
          
          wasExistingUser = true;
        }
      }
    } catch (checkError) {
      
    }

    // Step 4: FIXED APPROACH - Set External ID BEFORE or DURING initialization
    if (!wasExistingUser) {
      
      
      // First attempt: Set External ID if plugin is already somewhat initialized
      try {
        await adapter.login(supabaseUserId);
        
      } catch (preLoginError) {
        
      }
    }

    // Step 5: Initialize or ensure OneSignal SDK is ready
    try {
      await adapter.setAppId(oneSignalAppId);
      
    } catch (initError) {
      
      return { 
        success: false, 
        permissionGranted: false,
        message: 'Failed to initialize notification service' 
      };
    }

    // Step 6: ENSURE External ID is set immediately after initialization
    if (!wasExistingUser) {
      
      try {
        await adapter.login(supabaseUserId);
        
        
        // Give OneSignal time to process the External ID binding
        await new Promise(resolve => setTimeout(resolve, 1500));
      } catch (loginError) {
        
        // Continue with flow but this is a major issue
      }
    }

    // Step 7: Set up notification handlers
    setupNotificationHandlers(adapter);

    // Step 8: Check if this is a new device for the user
    const isNewDevice = wasExistingUser ? false : await checkIfNewDevice(supabaseUserId);

    // Step 9: Check current permission status
    const currentPermission = await getNotificationPermissionStatus();
    

    // Step 10: Handle permission request based on options
    let finalPermissionGranted = currentPermission;
    let requiresPermissionRequest = false;

    if (!currentPermission) {
      if (requestPermissionImmediately) {
        finalPermissionGranted = await requestPermissionWithCustomUI(adapter, customPermissionRequest);
      } else {
        requiresPermissionRequest = true;
      }
    }

    // Step 11: Get OneSignal device ID and verify External ID binding
    let oneSignalUserId: string | undefined;
    try {
      // Wait a bit more for External ID to be fully processed
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const deviceState = await adapter.getDeviceState();
      oneSignalUserId = deviceState?.userId;

      // VERIFICATION: Check that External ID is properly set
      const externalUserId = deviceState?.externalUserId;
      if (externalUserId !== supabaseUserId) {
        
        // Try to fix it one more time
        try {
          await adapter.login(supabaseUserId);
          
        } catch (fixError) {
          
        }
      } else {
        
      }

      if (oneSignalUserId) {
        await saveOneSignalUserId(oneSignalUserId);
        
      }
    } catch (deviceError) {
      
    }

    // Step 12: Update user profile in database with device tracking
    await updateUserNotificationStatus(supabaseUserId, {
      notificationEnabled: finalPermissionGranted,
      permissionAsked: requestPermissionImmediately || currentPermission,
      oneSignalUserId,
      lastLoginDevice: await getDeviceInfo(),
      externalIdVerified: true // Track that External ID was properly set
    });

    // Step 13: Show user feedback
    if (!silentMode && showSuccessToast) {
      showUserFeedback(finalPermissionGranted, isNewDevice, requiresPermissionRequest, wasExistingUser, showToast);
    }

    const result: OneSignalAuthResult = {
      success: true,
      permissionGranted: finalPermissionGranted,
      oneSignalUserId,
      requiresPermissionRequest,
      isNewDevice,
      wasExistingUser,
      message: createSuccessMessage(finalPermissionGranted, isNewDevice, requiresPermissionRequest, wasExistingUser)
    };

    
    return result;

  } catch (error) {
    
    return {
      success: false,
      permissionGranted: false,
      message: error instanceof Error ? error.message : 'Unknown error during notification setup'
    };
  }
};

/**
 * Enhanced OneSignal Logout with proper External ID removal
 */
export const enhancedOneSignalLogout = async (options: {
  silentMode?: boolean;
  timeout?: number;
  showToast?: ToastCallback; // ✅ FIX: Add toast callback
} = {}): Promise<{ success: boolean; message: string }> => {
  const { silentMode = false, timeout = 5000, showToast } = options;

  try {
    if (!Capacitor.isNativePlatform()) {
      return { success: true, message: 'Not on native platform' };
    }

    const adapter = getOneSignalAdapter();
    if (!adapter.isAvailable()) {
      return { success: true, message: 'OneSignal not available' };
    }

    

    // Create timeout promise
    const logoutPromise = adapter.logout();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Logout timeout')), timeout);
    });

    try {
      await Promise.race([logoutPromise, timeoutPromise]);
      
      
      if (!silentMode) {
        // ✅ FIX: Use showToast callback instead of toast hook
        showToast?.({
          title: "Signed Out",
          description: "Your notification preferences have been cleared"
        });
      }

      return { success: true, message: 'OneSignal logout successful - External ID removed' };
    } catch (logoutError) {
      
      return { success: true, message: 'Logout completed with warnings' };
    }

  } catch (error) {
    
    return { success: true, message: 'Logout completed despite errors' };
  }
};

/**
 * Request notification permission with enhanced UI and platform detection
 */
export const requestNotificationPermissionWithPrompt = async (
  customRequest?: OneSignalPermissionRequest,
  showToast?: ToastCallback // ✅ FIX: Add toast callback
): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const adapter = getOneSignalAdapter();
  if (!adapter.isAvailable()) {
    return false;
  }

  try {
    // Get platform information for better UX
    const platformInfo = {
      platform: isIOS() ? 'ios' : isAndroid() ? 'android' : 'unknown',
      isIOS: isIOS(),
      isAndroid: isAndroid()
    };

    console.log(`🔔 Requesting notification permission on ${platformInfo.platform}`);

    // Show platform-specific custom prompt if provided
    if (customRequest) {
      const userChoice = await showCustomPermissionPrompt(customRequest, platformInfo);
      if (!userChoice) {
        console.log(`❌ User declined notification permission on ${platformInfo.platform}`);
        return false;
      }
    }

    // Request permission from OneSignal with platform-specific handling
    console.log(`📱 Calling OneSignal permission request for ${platformInfo.platform}`);
    const granted = await adapter.promptForPushNotificationsWithUserResponse(true);
    
    if (granted) {
      console.log(`✅ Notification permission granted on ${platformInfo.platform}`);
      
      // Platform-specific success feedback
      const successMessage = platformInfo.isIOS 
        ? "🍎 iOS notifications enabled! You'll receive project updates and reminders."
        : platformInfo.isAndroid
        ? "🤖 Android notifications enabled! You'll get updates about your construction projects."
        : "🔔 Notifications enabled! You'll now receive important updates and reminders";

      // ✅ FIX: Use showToast callback instead of toast hook
      showToast?.({
        title: "Notifications Enabled! 🔔",
        description: successMessage
      });
    } else {
      console.log(`❌ Notification permission denied on ${platformInfo.platform}`);
      
      // Platform-specific denial feedback
      const denialMessage = platformInfo.isIOS
        ? "To enable later, go to Settings > Notifications > SantiyeX on your iPhone/iPad."
        : platformInfo.isAndroid
        ? "To enable later, go to Android Settings > Apps > SantiyeX > Notifications."
        : "You can enable them later in Settings";

      // ✅ FIX: Use showToast callback instead of toast hook
      showToast?.({
        title: "Notifications Disabled",
        description: denialMessage,
        variant: "destructive"
      });
    }

    return granted;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Helper Functions

const checkIfNewDevice = async (userId: string): Promise<boolean> => {
  try {
    const { data } = await supabase
      .from('users')
      .select('onesignal_user_id')
      .eq('id', userId)
      .single();
    
    return !data?.onesignal_user_id;
  } catch {
    return true; // Assume new device if we can't check
  }
};

const setupNotificationHandlers = (adapter: any): void => {
  try {
    // Foreground notification handler
    adapter.setNotificationWillShowInForegroundHandler((event: any) => {
      
      if (event?.notification?.complete) {
        event.notification.complete(OSNotificationAction.Display);
      }
    });

    // Notification opened handler
    adapter.setNotificationOpenedHandler((event: any) => {
      
      handleNotificationOpen(event);
    });

    
  } catch (error) {
    
  }
};

const handleNotificationOpen = (event: any): void => {
  try {
    const data = event?.notification?.additionalData;
    if (!data) return;

    // Handle different notification types
    if (data.type === 'payment_reminder') {
      // Navigate to payments page
      window.location.href = '/payments';
    } else if (data.type === 'project_update') {
      // Navigate to specific project
      if (data.projectId) {
        window.location.href = `/projects/${data.projectId}`;
      }
    } else if (data.type === 'system_announcement') {
      // Show announcement modal or navigate to announcements
      window.location.href = '/announcements';
    }
    // Add more notification types as needed
  } catch (error) {
    
  }
};

const requestPermissionWithCustomUI = async (
  adapter: any, 
  customRequest?: OneSignalPermissionRequest
): Promise<boolean> => {
  try {
    if (customRequest) {
      const userChoice = await showCustomPermissionPrompt(customRequest);
      if (!userChoice) return false;
    }

    return await adapter.promptForPushNotificationsWithUserResponse(true);
  } catch (error) {
    
    return false;
  }
};

const showCustomPermissionPrompt = async (
  request: OneSignalPermissionRequest,
  platformInfo?: { platform: string; isIOS: boolean; isAndroid: boolean }
): Promise<boolean> => {
  return new Promise((resolve) => {
    // Create platform-specific prompt message
    let promptMessage = `${request.title}\n\n${request.message}`;
    
    if (platformInfo) {
      if (platformInfo.isIOS) {
        promptMessage += '\n\n📱 This will show the iOS notification permission dialog.';
      } else if (platformInfo.isAndroid) {
        promptMessage += '\n\n🤖 This will request Android notification access.';
      }
    }

    // For now, using a simple confirm dialog - could be enhanced with a custom modal
    const result = confirm(promptMessage);
    resolve(result);
  });
};

const updateUserNotificationStatus = async (
  userId: string,
  status: {
    notificationEnabled: boolean;
    permissionAsked: boolean;
    oneSignalUserId?: string;
    lastLoginDevice?: any;
    externalIdVerified?: boolean;
  }
): Promise<void> => {
  try {
    const updateData: any = {
      notification_enabled: status.notificationEnabled,
      notification_permission_asked: status.permissionAsked,
      updated_at: new Date().toISOString(),
      last_login_device: status.lastLoginDevice ? JSON.stringify(status.lastLoginDevice) : null,
      external_id_verified: status.externalIdVerified || false
    };

    if (status.oneSignalUserId) {
      updateData.onesignal_user_id = status.oneSignalUserId;
    }

    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);

    if (error) {
      
    } else {
      
    }
  } catch (dbError) {
    
  }
};

const getDeviceInfo = async (): Promise<any> => {
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return {
      platform: info.platform,
      model: info.model,
      osVersion: info.osVersion,
      manufacturer: info.manufacturer
    };
  } catch {
    return { platform: Capacitor.getPlatform() };
  }
};

/**
 * Enhanced user feedback with External ID status
 */
const showUserFeedback = (
  permissionGranted: boolean, 
  isNewDevice: boolean, 
  requiresPermissionRequest: boolean,
  wasExistingUser: boolean = false,
  showToast?: ToastCallback // ✅ FIX: Add toast callback parameter
): void => {
  if (wasExistingUser && permissionGranted) {
    // ✅ FIX: Use showToast callback instead of toast hook
    showToast?.({
      title: "Welcome Back! 🎉",
      description: "Your notifications are active and properly synced",
    });
  } else if (isNewDevice && permissionGranted) {
    // ✅ FIX: Use showToast callback instead of toast hook
    showToast?.({
      title: "Setup Complete! ✅",
      description: "You'll receive notifications on this new device",
    });
  } else if (requiresPermissionRequest) {
    // ✅ FIX: Use showToast callback instead of toast hook
    showToast?.({
      title: "Almost Ready! 🔔",
      description: "Enable notifications in Settings to stay updated",
    });
  } else if (permissionGranted) {
    // ✅ FIX: Use showToast callback instead of toast hook
    showToast?.({
      title: "Notifications Active ✅",
      description: "You're all set to receive updates",
    });
  }
};

/**
 * Enhanced success message with External ID info
 */
const createSuccessMessage = (
  permissionGranted: boolean,
  isNewDevice: boolean,
  requiresPermissionRequest: boolean,
  wasExistingUser: boolean = false
): string => {
  if (wasExistingUser) {
    return permissionGranted 
      ? 'OneSignal reconnected successfully - same user maintained'
      : 'OneSignal reconnected - notifications disabled';
  }
  
  if (isNewDevice) {
    return permissionGranted 
      ? 'OneSignal configured for new device with External ID'
      : 'OneSignal ready for new device - permission needed';
  }
  
  return permissionGranted 
    ? 'OneSignal authenticated with External ID successfully'
    : 'OneSignal ready - permission request pending';
}; 